import React, { useState, useEffect } from 'react';
import styles from './CANguard.module.css';
import GuardBar from 'containers/pages/CANguard/components/navbar/GuardBar';
import CanTab from 'containers/cantabs/components/CanTab';
import { useMediaQuery, useTheme } from '@mui/material';
import {
	getCanTabState,
	setCanTabState,
} from 'containers/cantabs/libs/useLocalStorage';
import PatrolsTabV4 from 'containers/pages/CANguard/components/tabs/patrolsTab/PatrolsTabV4';
import PhoneTab from 'containers/pages/CANguard/components/tabs/phoneTab/PhoneTab';
import NotificationsTab from 'containers/pages/CANguard/components/tabs/notificationsTab/NotificationsTab';
import IncidentTab from 'containers/pages/CANguard/components/tabs/incidentTab/IncidentTab';
import ScheduleTab from 'containers/pages/CANguard/components/tabs/scheduleTab/ScheduleTab';
import AdminTab from 'containers/pages/CANguard/components/tabs/adminTab/AdminTab';

import {
	ScheduleProvider,
	useScheduleContext,
} from 'containers/pages/CANguard/context/ScheduleProvider';
import LoadingBackdrop from 'components/LoadingBackdrop';
import ActivePatrolDrawer from 'containers/pages/CANguard/components/activePatrolDrawer/ActivePatrolDrawer';
import { ActivePatrolUIProvider } from 'containers/pages/CANguard/context/activePatrol/ActivePatrolUIProvider';
import { ActivePatrolDataProvider } from 'containers/pages/CANguard/context/activePatrol/ActivePatrolDataProvider';
import { ActivePatrolLogicProvider } from 'containers/pages/CANguard/context/activePatrol/ActivePatrolLogicProvider';
import ActivePatrolWarning from 'containers/pages/CANguard/components/alertDialog/ActivePatrolWarning';
import {
	GuardRoleProvider,
	useGuardRoleContext,
} from 'containers/pages/CANguard/context/GuardRoleProvider';
import UploadTab from 'containers/pages/CANguard/components/tabs/uploadTab/UploadTab';
import { AdminUploaderProvider } from 'containers/pages/CANguard/context/AdminUploaderProvider';
import { CancamProvider } from 'containers/pages/CANguard/context/cancam/CancamProvider';
import { CancamDrawer } from 'containers/pages/CANguard/components/cancam';

export enum GuardTabs {
	AdminPanel = 'AdminPanel',
	Patrols = 'Patrols',
	Incident = 'Incident',
	Schedule = 'Schedule',
	Notifications = 'Notifications',
	Call = 'Call',
	Upload = 'Upload',
}

type GuardTabComponents = {
	[key in GuardTabs]: React.ReactNode;
};

function useCanTabs() {
	const [isCanTabOpen, setIsCanTabOpen] = useState<Boolean>(true);
	const theme = useTheme();
	const isMd = useMediaQuery(theme.breakpoints.down('md'));

	const onOpenCanTab = () => {
		const canTabSettings = getCanTabState();
		canTabSettings.menuState = 'open';
		setCanTabState(canTabSettings);
		setIsCanTabOpen(true);
	};

	const onCloseCanTab = () => {
		const canTabSettings = getCanTabState();
		canTabSettings.menuState = 'closed';
		setCanTabState(canTabSettings);
		setIsCanTabOpen(false);
	};

	return { isCanTabOpen, isMd, onOpenCanTab, onCloseCanTab };
}

function CANguard() {
	// This component now only wraps other components with the AdminProvider
	if (process.env.NODE_ENV === 'production') {
		document.title = 'Guard App V3 - CANdispatch';
	} else {
		document.title = 'Guard V3.2 (Local)';
	}
	return (
		<GuardRoleProvider>
			<AdminUploaderProvider>
				<ScheduleProvider>
					<ActivePatrolUIProvider>
						<ActivePatrolDataProvider>
							<CancamProvider>
								<ActivePatrolLogicProvider>
									<CANguardContent />
								</ActivePatrolLogicProvider>
							</CancamProvider>
						</ActivePatrolDataProvider>
					</ActivePatrolUIProvider>
				</ScheduleProvider>
			</AdminUploaderProvider>
		</GuardRoleProvider>
	);
}

function CANguardContent() {
	const { isAdmin } = useGuardRoleContext();
	const { isLoadingSchedule } = useScheduleContext();
	const { isCanTabOpen, onOpenCanTab, onCloseCanTab, isMd } = useCanTabs();
	const [activeTab, setActiveTab] = useState<GuardTabs>(GuardTabs.Patrols);

	const goToTab = (tab: GuardTabs) => {
		setActiveTab(tab);
	};

	const handleTabChange = (tab: GuardTabs) => {
		setActiveTab(tab);
	};

	useEffect(() => {
		if (isAdmin) {
			setActiveTab(GuardTabs.AdminPanel);
		} else {
			setActiveTab(GuardTabs.Patrols);
		}
	}, [isAdmin]);

	const tabComponents: GuardTabComponents = {
		AdminPanel: <AdminTab goToTab={goToTab} />,
		Patrols: <PatrolsTabV4 />,
		Call: <PhoneTab />,
		Notifications: <NotificationsTab />,
		Incident: <IncidentTab goToTab={goToTab} />,
		Schedule: <ScheduleTab goToTab={goToTab} />,
		Upload: <UploadTab goToTab={goToTab} />,
	};

	return (
		<main className={styles.shared_layout}>
			<CanTab
				open={isCanTabOpen}
				onOpen={onOpenCanTab}
				onClose={onCloseCanTab}
				isMd={isMd}
			/>
			<section className={styles.right_section}>
				<GuardBar
					activeTab={activeTab}
					handleTabChange={handleTabChange}
					handleMenuClick={onOpenCanTab}
				/>
				{/* Always render the tabs first but control its visibility. This way the state inside is preserved */}
				{Object.entries(tabComponents).map(([key, component]) => (
					<div
						key={key}
						className={styles.content}
						style={{ display: activeTab === key ? 'flex' : 'none' }}
					>
						{component}
					</div>
				))}
				<ActivePatrolDrawer />
				<CancamDrawer />
				<ActivePatrolWarning />
			</section>
			<LoadingBackdrop open={isLoadingSchedule} />
		</main>
	);
}

export default CANguard;
